import { cn } from "@tellescope/ui";
import { LogOut } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Page } from "../../shared/schema";

interface PortalSidebarProps {
  pages: Page[];
  currentPage: Page | null;
  onNavigate: (pageId: string) => void;
  portalName?: string;
  onLogout?: () => void;
  /** Organization logo URL - displayed instead of text when available */
  logoURL?: string;
  /** Organization name - used as fallback when no logo is available */
  organizationName?: string;
  /**
   * When true, renders navigation items as <Link> components for native link behavior
   * When false, renders as buttons that call onNavigate callback.
   */
  useLinks?: boolean;
}

export const PortalSidebar: React.FC<PortalSidebarProps> = ({
  pages,
  currentPage,
  onNavigate,
  portalName,
  onLogout,
  logoURL,
  organizationName,
  useLinks = false,
}) => {
  const { t } = useTranslation();

  // Get the first page slug to determine when to use "/" vs "/{slug}"
  const firstPageSlug = pages[0]?.slug;

  // Get the URL path for a page
  const getPagePath = (page: Page): string => {
    return page.slug === firstPageSlug ? "/" : `/${page.slug}`;
  };

  // Display name: use organization name if available, fall back to portal name, then default
  const displayName = organizationName || portalName || t("My Portal");

  return (
    <aside className="w-64 h-full shrink-0 border-r bg-primary/5 flex flex-col">
      {/* Logo / Organization Header */}
      <div className="px-6 py-8">
        {logoURL ? (
          <img src={logoURL} alt={displayName} className="h-10 max-w-[180px] object-contain" />
        ) : (
          <span className="text-lg font-semibold text-foreground">{displayName}</span>
        )}
      </div>

      {/* Navigation Links */}
      <nav className="flex-1 overflow-y-auto px-6 py-4">
        <div className="space-y-0">
          {pages.map((page) => {
            const isActive = currentPage?.id === page.id;
            const navItemClasses = cn(
              "w-full text-left py-3 text-sm transition-colors block",
              isActive ? "text-primary font-medium" : "text-muted-foreground hover:text-foreground"
            );

            return useLinks ? (
              <Link key={page.id} to={getPagePath(page)} className={navItemClasses}>
                {page.title}
              </Link>
            ) : (
              <button key={page.id} onClick={() => onNavigate(page.id)} className={navItemClasses}>
                {page.title}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Logout Section */}
      {onLogout && (
        <div className="px-6 py-4 border-t">
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-2 py-3 text-sm text-muted-foreground hover:text-destructive transition-colors"
          >
            <LogOut className="h-4 w-4" />
            {t("Log out")}
          </button>
        </div>
      )}
    </aside>
  );
};
